// ─────────────────────────────────────────────────────────────────────────────
// useWorkMutations
//
// Cross-entity TanStack Query mutation helpers for the Work pipeline.
//
// Why this exists (FND-state-1, FND-state-4, FND-state-12):
//   Deals/Quotes/Jobs/Invoices are zipped into a single Work pipeline. A
//   transition on one entity (e.g. quote ACCEPTED → triggers job creation
//   downstream; job COMPLETED → unlocks invoice; deal QUALIFIED → ready for
//   quote) cascades through the unified record set. Mutations that only
//   invalidate their own entity leave the other three stale.
//
// The hooks here:
//   - useStageAdvance: optimistic stage transition with cross-entity
//     invalidation on settle (success OR error, after rollback).
//   - useArchiveRecord: archive a quote (DECLINED), job (CANCELLED) or invoice
//     (CANCELLED). Deals are not archived here — marking one Lost requires a
//     reason and goes through the Mark as Lost modal.
//   - useDeleteRecord: delete the underlying entity, remove its detail cache,
//     and invalidate every list.
//   - useSetServiceType: re-type a record's service category (deal / quote /
//     job) with cross-entity invalidation.
//
// All three coexist with the page-level useUnifiedWorkRecords.refetch().
// Callers can still call refetch() if they need to await the merged result;
// the mutations alone keep every other mounted surface (Jobs page, Invoices
// page, Calendar) in sync without a manual refetch.
// ─────────────────────────────────────────────────────────────────────────────

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import {
  WORK_QUERY_KEYS,
  invalidateWorkEntities,
} from './useApi'
import type { ApiQuote, ApiJob, ApiInvoice, ApiDeal, PaginatedApiResponse } from '@/lib/apiTypes'

// ─────────────────────────────────────────────────────────────────────────────
// Stage advance — used by inline arrow / kanban drag in WorkPage
// ─────────────────────────────────────────────────────────────────────────────

export type StageAdvanceEntity = 'quote' | 'job' | 'invoice' | 'deal'

export interface StageAdvanceVariables {
  entity: StageAdvanceEntity
  id: string
  /** Patch body to send (e.g. { status: 'ACCEPTED' } or { stage: 'QUALIFIED' }) */
  patch: Record<string, unknown>
}

const ENDPOINT_BY_ENTITY: Record<StageAdvanceEntity, string> = {
  quote: '/api/quotes',
  job: '/api/jobs',
  invoice: '/api/invoices',
  deal: '/api/deals',
}

type ListSnapshot =
  | PaginatedApiResponse<ApiQuote>
  | PaginatedApiResponse<ApiJob>
  | PaginatedApiResponse<ApiInvoice>
  | PaginatedApiResponse<ApiDeal>

interface RolledBackQuery {
  queryKey: readonly unknown[]
  previous: unknown
}

/**
 * Stage-advance mutation with optimistic update + cross-entity invalidation.
 *
 * Optimistic update: walks every cached list for the targeted entity and
 * shallow-merges the patch onto the matching row in-place. If the server
 * rejects the transition, onError rolls each cache back to its pre-mutation
 * snapshot. onSettled then invalidates ALL four Work entities so the canonical
 * server state lands.
 */
export function useStageAdvance() {
  const queryClient = useQueryClient()

  return useMutation<unknown, Error, StageAdvanceVariables, { rolled: RolledBackQuery[] }>({
    mutationFn: async ({ entity, id, patch }) => {
      const res = await apiClient.patch(`${ENDPOINT_BY_ENTITY[entity]}/${id}`, patch)
      return res.data
    },
    onMutate: async ({ entity, id, patch }) => {
      // Cancel any in-flight refetches so they don't overwrite our optimistic value.
      await queryClient.cancelQueries({ queryKey: [entity === 'deal' ? 'deals' : `${entity}s`] })

      const listKey = entity === 'deal' ? WORK_QUERY_KEYS.deals.all
        : entity === 'quote' ? WORK_QUERY_KEYS.quotes.all
        : entity === 'job' ? WORK_QUERY_KEYS.jobs.all
        : WORK_QUERY_KEYS.invoices.all

      const rolled: RolledBackQuery[] = []

      // Apply optimistic patch to every cached list query for this entity.
      queryClient.setQueriesData<ListSnapshot>(
        { queryKey: listKey },
        (old) => {
          if (!old || typeof old !== 'object' || !('items' in old)) return old
          rolled.push({ queryKey: listKey, previous: old })
          return {
            ...old,
            items: (old.items as Array<{ id: string }>).map((row) =>
              row.id === id ? { ...row, ...patch } : row,
            ),
          } as ListSnapshot
        },
      )

      // Apply to the detail query too if it exists.
      const detailKey = entity === 'deal' ? WORK_QUERY_KEYS.deals.detail(id)
        : entity === 'quote' ? WORK_QUERY_KEYS.quotes.detail(id)
        : entity === 'job' ? WORK_QUERY_KEYS.jobs.detail(id)
        : WORK_QUERY_KEYS.invoices.detail(id)
      const detailPrev = queryClient.getQueryData(detailKey)
      if (detailPrev && typeof detailPrev === 'object') {
        rolled.push({ queryKey: detailKey, previous: detailPrev })
        queryClient.setQueryData(detailKey, { ...detailPrev, ...patch })
      }

      return { rolled }
    },
    onError: (_err, _vars, context) => {
      // Roll every snapshotted query back to its pre-mutation state.
      if (!context) return
      for (const { queryKey, previous } of context.rolled) {
        queryClient.setQueryData(queryKey, previous)
      }
    },
    onSettled: () => {
      // Cross-entity invalidation: stage advance on one entity can cascade
      // to the others (quote ACCEPTED creates a job skeleton on the backend;
      // job COMPLETED unlocks invoice creation, etc.) so refresh all four.
      invalidateWorkEntities(queryClient)
    },
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Archive (soft-cancel) — sets status/stage to a terminal-cancelled value
// ─────────────────────────────────────────────────────────────────────────────

export function useArchiveRecord() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, { entity: StageAdvanceEntity; id: string }>({
    mutationFn: async ({ entity, id }) => {
      if (entity === 'deal') {
        throw new Error('Mark a deal lost from the Lost reason dialog. A silent stage change is not allowed.')
      }
      const body =
        entity === 'invoice' ? { status: 'CANCELLED' }
        : entity === 'job' ? { status: 'CANCELLED' }
        : { status: 'DECLINED' }
      const res = await apiClient.patch(`${ENDPOINT_BY_ENTITY[entity]}/${id}`, body)
      return res.data
    },
    onSuccess: () => invalidateWorkEntities(queryClient),
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Delete — hard-removes the underlying entity
// ─────────────────────────────────────────────────────────────────────────────

export function useDeleteRecord() {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, { entity: StageAdvanceEntity; id: string }>({
    mutationFn: async ({ entity, id }) => {
      const res = await apiClient.delete(`${ENDPOINT_BY_ENTITY[entity]}/${id}`)
      return res.data
    },
    onSuccess: (_data, { entity, id }) => {
      // Drop the detail cache for this row outright so a stale subscriber
      // doesn't render a deleted entity.
      const detailKey =
        entity === 'deal' ? WORK_QUERY_KEYS.deals.detail(id)
        : entity === 'quote' ? WORK_QUERY_KEYS.quotes.detail(id)
        : entity === 'job' ? WORK_QUERY_KEYS.jobs.detail(id)
        : WORK_QUERY_KEYS.invoices.detail(id)
      queryClient.removeQueries({ queryKey: detailKey })
      invalidateWorkEntities(queryClient)
    },
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Service type — PATCH { serviceCategoryId } on the entity behind a record
// ─────────────────────────────────────────────────────────────────────────────

export type ServiceTypeEntity = 'deal' | 'quote' | 'job'

export interface ServiceTypeTarget {
  entity: ServiceTypeEntity
  id: string
}

/**
 * The entities to update when a unified Work record's service type changes.
 *
 * The first target is the entity the record is backed by: `deal:` → the deal,
 * `q:` → the quote, `job:` → the job. Invoices carry no service type, so an
 * `inv:` record is backed by its job (else its quote).
 *
 * A job record's quote is included as a linked target: useUnifiedWorkRecords
 * shows a job's own type and falls back to its quote's, so clearing only the
 * job would let the quote's type reappear, and re-typing only the job would
 * leave the quote filed under the old trade on the Quotes page.
 */
export function serviceTypeTargets(record: {
  id: string
  dealId?: string
  quoteId?: string
  jobId?: string
}): ServiceTypeTarget[] {
  if (record.id.startsWith('deal:')) {
    return [{ entity: 'deal', id: record.dealId ?? record.id.slice('deal:'.length) }]
  }
  const targets: ServiceTypeTarget[] = []
  if (record.jobId) targets.push({ entity: 'job', id: record.jobId })
  if (record.quoteId) targets.push({ entity: 'quote', id: record.quoteId })
  return targets
}

export interface SetServiceTypeVariables {
  /** From serviceTypeTargets(); the first entry must succeed, the rest are best-effort. */
  targets: ServiceTypeTarget[]
  /** null clears the service type. */
  serviceCategoryId: string | null
}

export function useSetServiceType() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, SetServiceTypeVariables>({
    mutationFn: async ({ targets, serviceCategoryId }) => {
      const [primary, ...linked] = targets
      if (!primary) throw new Error('This record has nothing to update.')
      await apiClient.patch(`${ENDPOINT_BY_ENTITY[primary.entity]}/${primary.id}`, { serviceCategoryId })
      // Linked members follow; a failure here leaves the primary change in
      // place rather than reporting the whole edit as failed.
      await Promise.allSettled(
        linked.map((t) => apiClient.patch(`${ENDPOINT_BY_ENTITY[t.entity]}/${t.id}`, { serviceCategoryId })),
      )
    },
    onSettled: () => invalidateWorkEntities(queryClient),
  })
}
